import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { ErrorMessage } from './CommonUI';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// ─── Rarity colour helpers ─────────────────────────────────────────────────

const rarityColors = {
    mythical: 'text-purple-600 bg-purple-100',
    rare:     'text-blue-600 bg-blue-100',
    common:   'text-green-600 bg-green-100',
};

const getRarityColor = (rarity) => rarityColors[rarity] ?? 'text-gray-600 bg-gray-100';

// ─── Edit User Modal ───────────────────────────────────────────────────────

const EditUserModal = ({ user: targetUser, onClose, onSaved }) => {
    const [form, setForm] = useState({
        display_name: targetUser.display_name || '',
        username:     targetUser.username     || '',
        email:        targetUser.email        || '',
        bio:          targetUser.bio          || '',
        verified:     targetUser.verified     === 1 ? 1 : 0,
        is_admin:     targetUser.is_admin     === 1 ? 1 : 0,
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr]     = useState('');

    const handleChange = (field, value) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr('');
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_update_user.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ user_id: targetUser.user_id, ...form }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed to update user');
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <h3 className="text-base font-semibold text-gray-900">
                        Edit User: {targetUser.username}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
                    <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
                        {err && (
                            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                                <input
                                    type="text"
                                    value={form.display_name}
                                    onChange={e => handleChange('display_name', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    maxLength={100}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                                <input
                                    type="text"
                                    value={form.username}
                                    onChange={e => handleChange('username', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    maxLength={50}
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={e => handleChange('email', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                            <textarea
                                value={form.bio}
                                onChange={e => handleChange('bio', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                                maxLength={500}
                            />
                        </div>

                        <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={form.verified === 1}
                                    onChange={e => handleChange('verified', e.target.checked ? 1 : 0)}
                                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Verified</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={form.is_admin === 1}
                                    onChange={e => handleChange('is_admin', e.target.checked ? 1 : 0)}
                                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Admin</span>
                            </label>
                        </div>
                    </div>

                    {/* Fixed footer */}
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Main component ────────────────────────────────────────────────────────

const AdminUsers = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin Users');

    const [isLoading,     setIsLoading]     = useState(true);
    const [isAuthorized,  setIsAuthorized]  = useState(false);
    const [users,         setUsers]         = useState([]);
    const [stats,         setStats]         = useState({
        total_users:      0,
        new_this_week:    0,
        new_this_month:   0,
        active_this_month: 0,
    });
    const [filters,       setFilters]       = useState({ verified: '', is_admin: '', search: '' });
    const [pagination,    setPagination]    = useState({
        current_page: 1, total_pages: 1, total_users: 0, users_per_page: 50,
    });
    const [error,         setError]         = useState(null);

    // Trophy modal state
    const [selectedUser,       setSelectedUser]       = useState(null);
    const [showTrophyModal,    setShowTrophyModal]    = useState(false);
    const [userTrophies,       setUserTrophies]       = useState([]);
    const [allTrophies,        setAllTrophies]        = useState([]);
    const [isLoadingTrophies,  setIsLoadingTrophies]  = useState(false);

    // Edit modal state
    const [editingUser,  setEditingUser]  = useState(null);

    // ── Auth ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const verifyAdminAccess = async () => {
            if (authLoading) return;
            if (!user) { navigate('/'); return; }
            const isAdmin = await checkAdminStatus();
            if (!isAdmin) { navigate('/'); return; }
            setIsAuthorized(true);
            setIsLoading(false);
        };
        verifyAdminAccess();
    }, [user, authLoading, checkAdminStatus, navigate]);

    useEffect(() => {
        if (isAuthorized) loadUsersData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.current_page, isAuthorized]);

    // ── Data loading ──────────────────────────────────────────────────────
    const loadUsersData = async () => {
        try {
            setError(null);
            const params = new URLSearchParams();
            if (filters.verified)  params.append('verified',  filters.verified);
            if (filters.is_admin)  params.append('is_admin',  filters.is_admin);
            if (filters.search)    params.append('search',    filters.search);
            params.append('page',  pagination.current_page);
            params.append('limit', pagination.users_per_page);

            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_get_users.php?${params}`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to fetch users data');
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Failed to load users');

            setUsers(data.users);
            setStats(data.stats);
            setPagination(data.pagination);
        } catch (e) {
            console.error('Error loading users data:', e);
            setError(`Unable to load users data: ${e.message}`);
        }
    };

    // ── Trophy management ─────────────────────────────────────────────────
    const handleTrophyManagement = async (targetUser) => {
        setSelectedUser(targetUser);
        setShowTrophyModal(true);
        setIsLoadingTrophies(true);
        try {
            const res  = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_get_user_trophies.php?user_id=${targetUser.user_id}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Failed to load trophies');
            setUserTrophies(data.user_trophies);
            setAllTrophies(data.all_trophies);
        } catch (e) {
            setError(`Unable to load user trophies: ${e.message}`);
        } finally {
            setIsLoadingTrophies(false);
        }
    };

    const handleAssignTrophy = async (trophyId) => {
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_assign_trophy.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ user_id: selectedUser.user_id, trophy_id: trophyId }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to assign trophy');
            await handleTrophyManagement(selectedUser);
            await loadUsersData();
        } catch (e) {
            setError(`Failed to assign trophy: ${e.message}`);
        }
    };

    const handleRemoveTrophy = async (trophyId) => {
        if (!window.confirm('Remove this trophy from the user?')) return;
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_remove_trophy.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ user_id: selectedUser.user_id, trophy_id: trophyId }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to remove trophy');
            await handleTrophyManagement(selectedUser);
            await loadUsersData();
        } catch (e) {
            setError(`Failed to remove trophy: ${e.message}`);
        }
    };

    // ── Delete user ───────────────────────────────────────────────────────
    const handleDeleteUser = async (targetUser) => {
        if (!window.confirm(
            `Permanently delete "${targetUser.display_name || targetUser.username}"?\n\nThis cannot be undone.`
        )) return;
        try {
            const res  = await fetch(`${process.env.REACT_APP_API_URL}/admin_delete_user.php`, {
                method:      'POST',
                credentials: 'include',
                headers:     { 'Content-Type': 'application/json' },
                body:        JSON.stringify({ user_id: targetUser.user_id }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Failed to delete user');
            await loadUsersData();
        } catch (e) {
            setError(`Failed to delete user: ${e.message}`);
        }
    };

    // ── Filters ───────────────────────────────────────────────────────────
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    };

    // ── Loading / auth guard ──────────────────────────────────────────────
    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Verifying admin access...</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/admin" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-red-600">
                                <FontAwesomeIcon icon="home" className="w-4 h-4 mr-2" />
                                Admin Dashboard
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <FontAwesomeIcon icon="chevron-right" className="w-6 h-6 text-gray-400" />
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">User Management</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                        <p className="mt-1 text-gray-500">
                            Manage user accounts, permissions, and trophy assignments.
                        </p>
                    </div>
                    <Link
                        to="/admin/trophies"
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 hover:text-white transition-colors text-sm font-medium"
                    >
                        <FontAwesomeIcon icon={faTrophy} className="mr-2" />
                        Manage Trophies
                    </Link>
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-6">
                        <ErrorMessage message={error} onRetry={() => setError(null)} />
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
                    {[
                        { label: 'Total Users',    value: stats.total_users },
                        { label: 'New This Week',  value: stats.new_this_week },
                        { label: 'New This Month', value: stats.new_this_month },
                        { label: 'Active (30d)',   value: stats.active_this_month },
                    ].map(s => (
                        <div key={s.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                            <p className="text-xs font-medium text-gray-500 truncate">{s.label}</p>
                            <p className="mt-1 text-2xl font-semibold text-gray-900">{s.value}</p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg mb-6">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h2>
                    </div>
                    <div className="px-5 py-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Verification</label>
                                <select
                                    value={filters.verified}
                                    onChange={e => handleFilterChange('verified', e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    <option value="">All</option>
                                    <option value="1">Verified</option>
                                    <option value="0">Unverified</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={filters.is_admin}
                                    onChange={e => handleFilterChange('is_admin', e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    <option value="">All</option>
                                    <option value="1">Admins only</option>
                                    <option value="0">Regular users</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={e => handleFilterChange('search', e.target.value)}
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Username, email, or display name…"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Users</h2>
                        <span className="text-sm text-gray-500">
                            Showing {users.length} of {pagination.total_users} users
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Joined</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">Last Login</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Stats</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((u) => (
                                    <tr key={u.user_id} className="hover:bg-gray-50">

                                        {/* User */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 h-9 w-9">
                                                    {u.profile_picture_url ? (
                                                        <img className="h-9 w-9 rounded-full object-cover" src={u.profile_picture_url} alt="" />
                                                    ) : (
                                                        <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
                                                            <FontAwesomeIcon icon="user" className="text-gray-500 text-sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                                                        {u.display_name || u.username}
                                                    </div>
                                                    <div className="text-xs text-gray-500">@{u.username}</div>
                                                    <div className="text-xs text-gray-400 truncate max-w-[160px]">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col items-start gap-1">
                                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${u.verified === 1 ? 'text-green-800 bg-green-100' : 'text-yellow-800 bg-yellow-100'}`}>
                                                    {u.verified === 1 ? 'Verified' : 'Unverified'}
                                                </span>
                                                {u.is_admin === 1 && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full text-purple-800 bg-purple-100">
                                                        Admin
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Joined */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                            {formatDate(u.join_date)}
                                        </td>

                                        {/* Last Login */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 hidden sm:table-cell">
                                            {formatDate(u.last_login)}
                                        </td>

                                        {/* Stats */}
                                        <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                                            <div className="text-xs text-gray-500 space-y-0.5">
                                                <div className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon="cubes" className="text-gray-400 w-3" />
                                                    <span>{u.total_sets} sets</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faTrophy} className="text-gray-400 w-3" />
                                                    <span>{u.total_trophies} trophies</span>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    onClick={() => handleTrophyManagement(u)}
                                                    title="Manage Trophies"
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon={faTrophy} className="text-xs" />
                                                </button>
                                                <button
                                                    onClick={() => setEditingUser(u)}
                                                    title="Edit User"
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon="edit" className="text-xs" />
                                                </button>
                                                <Link
                                                    to={`/profile/${u.user_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="View Profile"
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon="eye" className="text-xs" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteUser(u)}
                                                    title="Delete Account"
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-red-100 bg-white text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon="trash" className="text-xs" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {users.length === 0 && (
                            <div className="text-center py-12">
                                <FontAwesomeIcon icon="users" className="mx-auto text-3xl text-gray-300 mb-3" />
                                <p className="text-sm text-gray-500">No users found matching your filters.</p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                            <p className="text-sm text-gray-500 hidden sm:block">
                                Page {pagination.current_page} of {pagination.total_pages}
                            </p>
                            <div className="flex gap-2 mx-auto sm:mx-0">
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                                    disabled={pagination.current_page === 1}
                                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    ‹ Prev
                                </button>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, current_page: Math.min(prev.total_pages, prev.current_page + 1) }))}
                                    disabled={pagination.current_page === pagination.total_pages}
                                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next ›
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Trophy Management Modal */}
            {showTrophyModal && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-base font-semibold text-gray-900">
                                Trophies for {selectedUser.display_name || selectedUser.username}
                            </h3>
                            <button
                                onClick={() => setShowTrophyModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                            >
                                <FontAwesomeIcon icon="times" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            {isLoadingTrophies ? (
                                <div className="flex justify-center items-center py-12">
                                    <FontAwesomeIcon icon="spinner" className="animate-spin text-2xl text-gray-400" />
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                            Current Trophies ({userTrophies.length})
                                        </h4>
                                        {userTrophies.length === 0 ? (
                                            <p className="text-sm text-gray-400 text-center py-6">No trophies assigned yet.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {userTrophies.map(t => (
                                                    <div key={t.id} className="flex items-start gap-3 border rounded-lg p-3 bg-yellow-50 border-yellow-200">
                                                        {t.image_path ? (
                                                            <img src={t.image_path} alt={t.name} className="w-8 h-8 rounded flex-shrink-0" />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTrophy} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                                                            <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full mt-0.5 ${getRarityColor(t.rarity)}`}>
                                                                {t.rarity}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleRemoveTrophy(t.id)}
                                                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                                                            title="Remove"
                                                        >
                                                            <FontAwesomeIcon icon="times" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                            Available ({allTrophies.filter(t => !t.user_has_trophy).length})
                                        </h4>
                                        {allTrophies.filter(t => !t.user_has_trophy).length === 0 ? (
                                            <p className="text-sm text-gray-400 text-center py-6">All trophies assigned.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {allTrophies.filter(t => !t.user_has_trophy).map(t => (
                                                    <div key={t.id} className="flex items-start gap-3 border rounded-lg p-3 hover:bg-gray-50">
                                                        {t.image_path ? (
                                                            <img src={t.image_path} alt={t.name} className="w-8 h-8 rounded flex-shrink-0" />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTrophy} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                                                            <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full mt-0.5 ${getRarityColor(t.rarity)}`}>
                                                                {t.rarity}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleAssignTrophy(t.id)}
                                                            className="text-green-500 hover:text-green-700 flex-shrink-0"
                                                            title="Assign"
                                                        >
                                                            <FontAwesomeIcon icon="plus" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSaved={loadUsersData}
                />
            )}
        </div>
    );
};

export default AdminUsers;
